import React, { useState } from "react";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "./firebase";
import "./CompetitionForm.css";
import { color } from "three/tsl";

export default function CompetitionForm({ competition, onClose }) {
  const isInterSchoolQuizComp = competition.title === "Inter School Quiz";

  const [formData, setFormData] = useState({
    participationType: isInterSchoolQuizComp ? "inter" : "",
    teamName: "",
    schoolName: "",
    participantName: "",
    email: "",
    clubEmail: "",
    phone: "",
    grade: "",
    hackerRankId: "",
    // For Inter School Quiz, require 4 additional members (teamMembers represents additional members)
    teamMembers: isInterSchoolQuizComp
      ? Array.from({ length: 4 }, () => ({ name: "", phone: "" }))
      : [{ name: "", phone: "" }],
    submissionLink: "",
    additionalInfo: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [submitErrorMessage, setSubmitErrorMessage] = useState("");

  const isTeamCompetition = [
    "Real-world Problem Solving",
    "Fastest Line Following Bot",
    "Innovation Challenge",
    "Inter School Quiz",
  ].includes(competition.title);

  const isSimpleForm = ["Armitage", "Insignia"].includes(competition.school);
  const isZenith = competition.school === "Zenith";
  const isOrthodox = competition.school === "Orthodox";
  const isAlgorithmChallenge = competition.title === "Algorithm";
  const isInterSchoolQuiz = competition.title === "Inter School Quiz";
  const maxAdditionalMembers = isOrthodox ? 4 : isInterSchoolQuizComp ? 4 : 3;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleTeamMemberChange = (index, field, value) => {
    const newTeamMembers = [...formData.teamMembers];
    newTeamMembers[index][field] = value;
    setFormData((prev) => ({ ...prev, teamMembers: newTeamMembers }));
  };

  const addTeamMember = () => {
    // For Inter School Quiz additional members are compulsory and fixed at 4
    if (isInterSchoolQuizComp) return;

    if (formData.teamMembers.length < maxAdditionalMembers) {
      setFormData((prev) => ({
        ...prev,
        teamMembers: [...prev.teamMembers, { name: "", phone: "" }],
      }));
    }
  };

  const removeTeamMember = (index) => {
    // Prevent removing members for Inter School Quiz (all 4 are required)
    if (isInterSchoolQuizComp) return;

    if (formData.teamMembers.length > 1) {
      const newTeamMembers = formData.teamMembers.filter((_, i) => i !== index);
      setFormData((prev) => ({ ...prev, teamMembers: newTeamMembers }));
    }
  };

  const whatsappLinks = {
    inter: {
      "Algorithm": "https://chat.whatsapp.com/BTIkY4TVxbHI51D745SGEd?mode=ems_copy_c",
      "Real-world Problem Solving": "https://chat.whatsapp.com/FuSpauahwSuAoxZfUFa2fb?mode=ems_copy_c",
      "Web Development": "https://chat.whatsapp.com/Di8b5b4oCjMLfmYDAZups8?mode=ems_copy_c",
      "Photo Manipulation": "https://chat.whatsapp.com/Cjd8UY7VkD2LxRQ9c3S8sf?mode=ems_copy_c",
      "Poster Design": "https://chat.whatsapp.com/KyzZnvzafCuESIEnalEgKS?mode=ems_copy_c",
      "Logo Design": "https://chat.whatsapp.com/CeaMFfdVRNP6mCq4pXLRWg?mode=ems_copy_c",
      "UI/UX Design": "https://chat.whatsapp.com/HEbsyaX0eeX6e9Sh4tB2xM?mode=ems_copy_c",
      "Fastest Line Following Bot": "https://chat.whatsapp.com/HPP21FKGTBeJdzu7TdTGr4?mode=ems_copy_c",
      "Innovation Challenge": "https://chat.whatsapp.com/DHXzQ08S09I5Ki7c4Dy7BN?mode=ems_copy_c",
    },
    intra: {
      "Algorithm": "https://chat.whatsapp.com/Dhqbk4fVxFaDVwrwLB75qL?mode=ems_copy_c",
      "Fastest Line Following Bot": "https://chat.whatsapp.com/Du90FeLuhnx6lEoa18tbUa?mode=ems_copy_c",
      "Innovation Challenge": "https://chat.whatsapp.com/HUYJKbGj64t4J1Lhpw2auS?mode=ems_copy_c",
      "Logo Design": "https://chat.whatsapp.com/JltjXe9Yd5O47IwiLNeXSZ?mode=ems_copy_c",
      "Photo Manipulation": "https://chat.whatsapp.com/Le6wUJB2W2r9NItRpWKTRd?mode=ems_copy_c",
      "Poster Design": "https://chat.whatsapp.com/D3T7Mz0aOrWBrEhv2kaYDK?mode=ems_copy_c",
      "Real-world Problem Solving": "https://chat.whatsapp.com/JPqpDTD2MaR8m4IeoWYxOq?mode=ems_copy_c",
      "UI/UX Design": "https://chat.whatsapp.com/GRqUnreByLWB2OWxKrzFZM?mode=ems_copy_c",
      "Web Development": "https://chat.whatsapp.com/DlyvsuuF2EXDEth9gZ2T8d?mode=ems_copy_c",
    },
  };

  const RNR_PDF_URL = "https://firebasestorage.googleapis.com/v0/b/btui-25.firebasestorage.app/o/BTUI%2726_RnR.pdf?alt=media&token=4d0cdc8c-d6df-4d86-b506-14562e94c17b";

  const rnrLinks = {
    inter: {
      "Algorithm": RNR_PDF_URL, 
      "Real-world Problem Solving": RNR_PDF_URL,
      "Web Development": RNR_PDF_URL,
      "Photo Manipulation": RNR_PDF_URL,
      "Poster Design": RNR_PDF_URL,
      "Logo Design": RNR_PDF_URL,
      "UI/UX Design": RNR_PDF_URL,
      "Fastest Line Following Bot": RNR_PDF_URL,
      "Innovation Challenge": RNR_PDF_URL,
      "Inter School Quiz": RNR_PDF_URL,
    },
    intra: {
      "Algorithm": RNR_PDF_URL,
      "Fastest Line Following Bot": RNR_PDF_URL,
      "Innovation Challenge": RNR_PDF_URL,
      "Logo Design": RNR_PDF_URL,
      "Photo Manipulation": RNR_PDF_URL,
      "Poster Design": RNR_PDF_URL,
      "Real-world Problem Solving": RNR_PDF_URL,
      "UI/UX Design": RNR_PDF_URL,
      "Web Development": RNR_PDF_URL,
    },
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Validation: if Inter School Quiz, ensure exactly 4 additional members and all filled
      if (isInterSchoolQuizComp) {
        const members = formData.teamMembers || [];
        if (members.length !== 4 || members.some((m) => !m.name.trim() || !m.phone.trim())) {
          setSubmitErrorMessage("Inter School Quiz requires 4 additional team members with name and contact.");
          setSubmitStatus("error");
          setIsSubmitting(false);
          return;
        }
      }

      const submissionData = {
        competitionTitle: competition.title,
        competitionSchool: competition.school,
        participationType: formData.participationType,
        teamName: formData.teamName || null,
        schoolName:
          formData.participationType === "intra"
            ? "Royal College"
            : formData.schoolName,
        participantName: formData.participantName,
        email: formData.email,
        clubEmail: formData.clubEmail || null,
        phone: formData.phone,
        grade: formData.grade,
        hackerRankId: formData.hackerRankId || null,
        teamMembers:
          formData.teamMembers && formData.teamMembers.length > 0
            ? formData.teamMembers
            : null,
        submissionLink: formData.submissionLink || null,
        additionalInfo: formData.additionalInfo || null,
        submittedAt: serverTimestamp(),
      };

      const collectionName = `registrations_${competition.title
        .toLowerCase()
        .replace(/\s+/g, "_")
        .replace(/\//g, "_")}`;

      await addDoc(collection(db, collectionName), submissionData);
      console.log("Form submitted successfully:", submissionData);
      setSubmitStatus("success");
    } catch (error) {
      console.error("Submission error:", error);
      setSubmitErrorMessage("Something went wrong. Please try again.");
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="form-overlay" onClick={onClose}>
      <div className="form-container" onClick={(e) => e.stopPropagation()}>
        <button className="form-close-btn" onClick={onClose}>
          ✕
        </button>

        <h2 className="form-title">Register for {competition.title}</h2>
        <p className="form-subtitle">{competition.school}</p>

        {/* Hide form after success */}
        {submitStatus !== "success" && (
          <form onSubmit={handleSubmit} className="competition-form">
            {!isInterSchoolQuiz && (
              <div className="form-group">
                <label htmlFor="participationType">Participation Type *</label>
                <select
                  id="participationType"
                  name="participationType"
                  value={formData.participationType}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select Participation Type</option>
                  <option value="inter">Inter School</option>
                  <option value="intra">Intra School</option>
                </select>
              </div>
            )}

            {!isSimpleForm && isTeamCompetition && !isInterSchoolQuizComp && (
              <div className="form-group">
                <label htmlFor="teamName">Team Name *</label>
                <input
                  type="text"
                  id="teamName"
                  name="teamName"
                  value={formData.teamName}
                  onChange={handleChange}
                  required
                />
              </div>
            )}

            <div className="form-group">
              <label htmlFor="participantName">
                {!isSimpleForm && isTeamCompetition
                  ? "Team Leader Name *"
                  : "Full Name *"}
              </label>
              <input
                type="text"
                id="participantName"
                name="participantName"
                value={formData.participantName}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="grade">Grade *</label>
              <select
                id="grade"
                name="grade"
                value={formData.grade}
                onChange={handleChange}
                required
              >
                <option value="">Select Grade</option>
                {[6, 7, 8, 9, 10, 11].map((grade) => (
                  <option key={grade} value={grade}>
                    Grade {grade}
                  </option>
                ))}
                <option value="After O/L">After O/L</option>
                {[12, 13].map((grade) => (
                  <option key={grade} value={grade}>
                    Grade {grade}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="email">Email Address *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>

            {!isSimpleForm && isTeamCompetition && (
              <div className="form-group">
                <label htmlFor="clubEmail">Club Email *</label>
                <input
                  type="email"
                  id="clubEmail"
                  name="clubEmail"
                  value={formData.clubEmail}
                  onChange={handleChange}
                  required
                />
              </div>
            )}

            <div className="form-group">
              <label htmlFor="phone">Contact Number *</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
              />
            </div>

            {formData.participationType === "inter" && (
              <div className="form-group">
                <label htmlFor="schoolName">School Name *</label>
                <input
                  type="text"
                  id="schoolName"
                  name="schoolName"
                  value={formData.schoolName}
                  onChange={handleChange}
                  required
                />
              </div>
            )}

            {isAlgorithmChallenge && (
              <div className="form-group">
                <label htmlFor="hackerRankId">HackerRank ID *</label>
                <input
                  type="text"
                  id="hackerRankId"
                  name="hackerRankId"
                  value={formData.hackerRankId}
                  onChange={handleChange}
                  required
                />
              </div>
            )}

            {!isSimpleForm && isTeamCompetition && (
              <div className="form-group">
                <label>
                  Additional Team Members {isInterSchoolQuizComp ? "(4 required)" : `(max ${maxAdditionalMembers})`}
                </label>
                {formData.teamMembers.map((member, index) => (
                  <div key={index} className="team-member-input">
                    <input
                      type="text"
                      value={member.name}
                      onChange={(e) =>
                        handleTeamMemberChange(index, "name", e.target.value)
                      }
                      placeholder={`Member ${index + 1} name`}
                      required={isInterSchoolQuizComp}
                    />
                    <input
                      type="tel"
                      value={member.phone}
                      onChange={(e) =>
                        handleTeamMemberChange(index, "phone", e.target.value)
                      }
                      placeholder={`Member ${index + 1} contact`}
                      required={isInterSchoolQuizComp}
                    />
                    {!isInterSchoolQuizComp && formData.teamMembers.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeTeamMember(index)}
                        className="remove-member-btn"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}
                {!isInterSchoolQuizComp && formData.teamMembers.length < maxAdditionalMembers && (
                  <button
                    type="button"
                    onClick={addTeamMember}
                    className="add-member-btn"
                  >
                    + Add Member
                  </button>
                )}
              </div>
            )}

            {!isSimpleForm && !isZenith && !isOrthodox && (
              <>
                <div className="form-group">
                  <label htmlFor="submissionLink">Submission Link</label>
                  <input
                    type="url"
                    id="submissionLink"
                    name="submissionLink"
                    value={formData.submissionLink}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="additionalInfo">Additional Info</label>
                  <textarea
                    id="additionalInfo"
                    name="additionalInfo"
                    value={formData.additionalInfo}
                    onChange={handleChange}
                    rows="4"
                  />
                </div>
              </>
            )}

            <div className="form-actions">
              <button type="submit" className="submit-btn" disabled={isSubmitting}>
                {isSubmitting ? "Submitting..." : "Register"}
              </button>
              <button type="button" className="cancel-btn" onClick={onClose}>
                Cancel
              </button>
            </div>
          </form>
        )}

        {submitStatus === "success" && (
         <div className="status-message success" style={{display: "flex", alignItems: "center", justifyContent: "center"}}><div>
  <p style={{ fontWeight: "bold", fontSize: "16px" }}>✓ Registration Successful!</p>
  {whatsappLinks[formData.participationType]?.[competition.title] && (
    <div style={{ marginTop: "15px", display: "flex", gap: "10px", flexWrap: "wrap" }}>
      <p>Join the WhatsApp group to stay updated:</p>

      <button
        type="button"
        className="whatsapp-btn"
        style={{
          padding: "10px 15px",
          background: "#25D366",
          color: "#fff",
          borderRadius: "5px",
          border: "none",
          fontWeight: "bold",
          cursor: "pointer"
        }}
      >
        <a
          href={whatsappLinks[formData.participationType][competition.title]}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "#fff", textDecoration: "none" }}
        >
          Join WhatsApp Group
        </a>
      </button>

      <button
        type="button"
        className="rnr-btn"
        style={{
          padding: "10px 15px",
          background: "#ffafafff",
          color: "#fff",
          border: "none",
          borderRadius: "5px",
          fontWeight: "bold",
          cursor: "pointer"
        }}
      >
        <a href={rnrLinks[formData.participationType]?.[competition.title] || RNR_PDF_URL} target="_blank"
          rel="noopener noreferrer"
          style={{ color: "#fff", textDecoration: "none" }}>Rules and Regulations</a>
        
      </button>
    </div>
  )}</div>
</div>

        )}

        {submitStatus === "error" && (
          <div className="status-message error">
            {submitErrorMessage || "Something went wrong. Please try again."}
          </div>
        )}
      </div>
    </div>
  );
}
